import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { requestVehicleLiveStream } from '../../lib/api/endpoints';
import { ApiError } from '../../lib/api/client';
import { useHlsPlayer } from '../../hooks/useHlsPlayer';
import { Spinner } from '../ui/Spinner';
import type { VehicleCamera } from '../../types/vehicle';

interface LiveVideoPlayerProps {
  vehicleId: string;
  camera: VehicleCamera;
  onClose: () => void;
}

type RequestState =
  | { kind: 'requesting' }
  | { kind: 'ready'; playlistUrl: string; expiresAt: string; delaySeconds: number }
  | { kind: 'expired' }
  | { kind: 'error'; message: string };

function describeStreamError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'No tienes permiso para ver video de este vehículo.';
    if (err.status === 409) return 'La cámara no está disponible para captura ahora mismo.';
    return err.detail ? `${err.title}: ${err.detail}` : err.title;
  }
  return err instanceof Error ? err.message : 'No se pudo iniciar la transmisión.';
}

// FE-18/#79. El vivo es una copia HLS del grabador: no se restablece el FLV
// directo porque abrirlo podría desplazar la captura continua del proveedor.
export function LiveVideoPlayer({ vehicleId, camera, onClose }: LiveVideoPlayerProps) {
  const [state, setState] = useState<RequestState>({ kind: 'requesting' });
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'requesting' });

    requestVehicleLiveStream(vehicleId, camera.id)
      .then(({ playlistUrl, expiresAt, delaySeconds }) => {
        if (cancelled) return;
        setState({ kind: 'ready', playlistUrl, expiresAt, delaySeconds });
        const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();
        if (msUntilExpiry > 0) {
          expiryTimerRef.current = setTimeout(() => {
            if (!cancelled) setState({ kind: 'expired' });
          }, msUntilExpiry);
        }
      })
      .catch(err => {
        if (!cancelled) setState({ kind: 'error', message: describeStreamError(err) });
      });

    return () => {
      cancelled = true;
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    };
  }, [vehicleId, camera.id]);

  const playlistUrl = state.kind === 'ready' ? state.playlistUrl : null;
  const { videoRef, error: playerError, isReconnecting, isSupported } = useHlsPlayer(playlistUrl);
  const errorMessage = state.kind === 'error' ? state.message : playerError;

  return (
    <div style={{
      position: 'relative', aspectRatio: '16/9', background: '#000', borderRadius: 'var(--radius-lg)',
      overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <video
        ref={videoRef}
        controls
        style={{ width: '100%', height: '100%', display: playlistUrl && !errorMessage && isSupported ? 'block' : 'none' }}
      />

      {state.kind === 'ready' && !errorMessage && isSupported && (
        <span style={{
          position: 'absolute', left: '8px', bottom: '8px', color: '#FFF', background: 'rgba(15,23,42,0.82)',
          borderRadius: '999px', padding: '4px 8px', fontSize: '11px', fontWeight: 700,
        }}>
          Video diferido · aprox. {state.delaySeconds}s
        </span>
      )}

      {state.kind === 'requesting' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', color: '#FFF' }}>
          <Spinner />
          <p style={{ fontSize: '13px', margin: 0 }}>Abriendo transmisión diferida…</p>
        </div>
      )}

      {!isSupported && playlistUrl && !errorMessage && (
        <PlayerMessage message="Este navegador no puede reproducir HLS autenticado." />
      )}
      {errorMessage && <PlayerMessage message={errorMessage} />}

      {/* #148: un corte del proveedor (cada ~40 s) no es un fallo. Mientras el
          reproductor reintenta, se dice; el video sigue visible debajo con lo
          último que cargó. */}
      {isReconnecting && !errorMessage && (
        <span
          role="status"
          style={{
            position: 'absolute', right: '8px', top: '8px', display: 'flex', alignItems: 'center', gap: '6px',
            color: '#FFF', background: 'rgba(15,23,42,0.82)', borderRadius: '999px', padding: '4px 10px',
            fontSize: '11px', fontWeight: 700,
          }}
        >
          <Spinner size="sm" /> Reconectando con la transmisión…
        </span>
      )}

      {state.kind === 'expired' && !errorMessage && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: '#FFF', padding: '1rem', textAlign: 'center',
        }}>
          <p style={{ fontSize: '13px', margin: 0 }}>La sesión de video expiró. Ciérrala y ábrela de nuevo.</p>
        </div>
      )}

      <button
        onClick={onClose}
        style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(15,23,42,0.75)', border: 'none', borderRadius: '6px', color: '#FFF', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        title="Cerrar transmisión"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function PlayerMessage({ message }: { message: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', color: '#FFF', padding: '1rem', textAlign: 'center' }}>
      <AlertTriangle size={28} style={{ color: '#F59E0B' }} />
      <p style={{ fontSize: '13px', margin: 0 }}>{message}</p>
    </div>
  );
}
